import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateGroup, deleteGroup, updateRow, deleteRow } from '../../store/slices/reportingSlice';

// ── Date utils ─────────────────────────────────────────────────────────────────

const parseDate = (str) => {
  if (!str) return null;
  return new Date(str + 'T12:00:00');
};

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((b - a) / 86400000);
};

const today = () => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = parseDate(dateStr);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const toISODate = (date) => {
  if (!date) return null;
  return date.toISOString().split('T')[0];
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Dependency & delay utilities ───────────────────────────────────────────

/**
 * Build maps of rowId → effective start/end date strings, propagating delays
 * through the dependency chain (topological order).
 * If actual_end_date is set and > end_date, that row is "delayed" and all
 * downstream dependents shift their start AND end by the same delta.
 * Returns { effectiveEnd, effectiveStart }.
 */
const buildEffectiveDates = (groups, dependencies) => {
  // Flat map of all rows by id
  const rowById = {};
  groups.forEach(g => (g.rows || []).forEach(r => { rowById[r.id] = r; }));

  // Build adjacency: predecessorId → [successorIds]
  const successors = {};
  const predecessors = {};
  dependencies.forEach(({ from_row_id, to_row_id }) => {
    if (!successors[from_row_id]) successors[from_row_id] = [];
    successors[from_row_id].push(to_row_id);
    if (!predecessors[to_row_id]) predecessors[to_row_id] = [];
    predecessors[to_row_id].push(from_row_id);
  });

  // Topological sort (Kahn's algorithm)
  const inDegree = {};
  Object.keys(rowById).forEach(id => { inDegree[id] = (predecessors[id] || []).length; });
  const queue = Object.keys(inDegree).filter(id => inDegree[id] === 0);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    (successors[id] || []).forEach(sid => {
      inDegree[sid]--;
      if (inDegree[sid] === 0) queue.push(String(sid));
    });
  }

  // Compute effective start and end dates in topological order
  const effectiveEnd = {};   // rowId → ISO date string
  const effectiveStart = {}; // rowId → ISO date string
  order.forEach(id => {
    const row = rowById[id];
    if (!row) return;
    const plannedEnd = row.end_date;
    const plannedStart = row.start_date;
    const actualEnd = row.actual_end_date;

    let myEffectiveEnd = plannedEnd;
    let myEffectiveStart = plannedStart;

    // If this row itself is delayed, extend its effective end
    if (actualEnd && plannedEnd && actualEnd > plannedEnd) {
      myEffectiveEnd = actualEnd;
    }

    // Find the maximum delay pushed from any predecessor
    const preds = predecessors[id] || [];
    let maxPredDelay = 0;
    preds.forEach(pid => {
      const predEffEnd = effectiveEnd[pid];
      if (!predEffEnd || !plannedEnd) return;
      const predPlannedEnd = rowById[pid]?.end_date;
      if (!predPlannedEnd || predEffEnd <= predPlannedEnd) return;
      const delay = Math.round(
        (new Date(predEffEnd + 'T12:00:00') - new Date(predPlannedEnd + 'T12:00:00')) / 86400000
      );
      if (delay > maxPredDelay) maxPredDelay = delay;
    });

    // Shift both start and end forward by the predecessor delay
    if (maxPredDelay > 0) {
      if (myEffectiveEnd) {
        const shifted = new Date(myEffectiveEnd + 'T12:00:00');
        shifted.setDate(shifted.getDate() + maxPredDelay);
        const shiftedStr = shifted.toISOString().split('T')[0];
        if (shiftedStr > myEffectiveEnd) myEffectiveEnd = shiftedStr;
      }
      if (myEffectiveStart) {
        const shifted = new Date(myEffectiveStart + 'T12:00:00');
        shifted.setDate(shifted.getDate() + maxPredDelay);
        myEffectiveStart = shifted.toISOString().split('T')[0];
      }
    }

    effectiveEnd[id] = myEffectiveEnd;
    effectiveStart[id] = myEffectiveStart;
  });

  return { effectiveEnd, effectiveStart };
};

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  'complete': 'Complete',
  'on-track': 'On Track',
  'at-risk': 'At Risk',
  'behind': 'Behind',
  'not-started': 'Not Started',
  'in-progress': 'In Progress',
  'blocked': 'Blocked',
};

const StatusBadge = ({ status }) => (
  <span className={`rp-status ${status}`}>{STATUS_LABELS[status] || status}</span>
);

// ── Mini progress bar ──────────────────────────────────────────────────────────

const MiniBar = ({ pct, color }) => (
  <div className="rp-mini-bar" style={{ flex: 1 }}>
    <div className="rp-mini-fill" style={{ width: `${pct}%`, background: color || 'var(--color-brand)' }} />
  </div>
);

// ── Assignee avatar ────────────────────────────────────────────────────────────

const AssigneeCell = ({ customName }) => {
  if (!customName) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: 10 }}>—</span>;
  const parts = customName.trim().split(' ');
  const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  return (
    <div className="rp-assignee" title={customName}>
      <div className="rp-avatar" style={{ background: '#64748b' }}>{initials || '?'}</div>
      <span className="rp-assignee-name">{parts[0] || customName}</span>
    </div>
  );
};

// ── Gantt Pane ─────────────────────────────────────────────────────────────────

// How many days to show in the viewport for each zoom level
const TARGET_VISIBLE_DAYS = { weeks: 28, months: 90, quarters: 270 };

const GanttPane = ({ groups, zoom, tableScrollRef, dependencies = [] }) => {
  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const syncRef = useRef(null);
  const [containerW, setContainerW] = useState(900);

  // Measure container width on mount, on zoom change, and on window resize
  useLayoutEffect(() => {
    const measure = () => {
      if (bodyRef.current) setContainerW(bodyRef.current.clientWidth || 900);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [zoom]); // eslint-disable-line

  const { effectiveEnd: effectiveDates, effectiveStart: effectiveStarts } = buildEffectiveDates(groups, dependencies);

  const allDates = groups.flatMap(g =>
    (g.rows || []).flatMap(r => {
      const effEnd = effectiveDates[r.id] || r.end_date;
      const effStart = effectiveStarts[r.id] || r.start_date;
      return [effStart, effEnd];
    }).filter(Boolean)
  );
  const todayStr = toISODate(today());

  const sortedDates = [...allDates, todayStr].filter(Boolean).sort();
  if (!sortedDates.length) {
    return (
      <div className="rp-gantt-panel">
        <div style={{ padding: 'var(--space-6)', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)', textAlign: 'center' }}>
          No date data yet — add start/end dates to tasks.
        </div>
      </div>
    );
  }

  const rangeStart = parseDate(sortedDates[0]);
  rangeStart.setDate(rangeStart.getDate() - 7);
  const rangeEnd = parseDate(sortedDates[sortedDates.length - 1]);
  rangeEnd.setDate(rangeEnd.getDate() + 14);

  const totalDays = Math.max(daysBetween(rangeStart, rangeEnd), 1);

  // pxPerDay driven by zoom: each level targets a different number of visible days.
  // containerW / targetDays = px needed per day to fill the viewport at that zoom level.
  const targetVisible = TARGET_VISIBLE_DAYS[zoom] || 90;
  const zoomPx = containerW > 100 ? containerW / targetVisible : 8;

  // Ensure the shortest task bar is at least 32px wide
  let minTaskDays = Infinity;
  groups.forEach(g => (g.rows || []).forEach(r => {
    if (r.start_date && r.end_date) {
      const d = Math.max(daysBetween(parseDate(r.start_date), parseDate(r.end_date)), 1);
      if (d < minTaskDays) minTaskDays = d;
    }
  }));
  if (!isFinite(minTaskDays)) minTaskDays = 1;
  const minForTasks = Math.ceil(32 / minTaskDays);

  const pxPerDay = Math.min(Math.max(zoomPx, minForTasks), 80);

  const totalW = totalDays * pxPerDay;

  const toX = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 0;
    return Math.round(daysBetween(rangeStart, d) * pxPerDay);
  };

  const todayX = toX(todayStr);

  // Vertical scroll sync
  useEffect(() => {
    const tableEl = tableScrollRef?.current;
    const ganttEl = bodyRef.current;
    if (!tableEl || !ganttEl) return;
    const fromTable = () => {
      if (syncRef.current === 'gantt') return;
      syncRef.current = 'table';
      ganttEl.scrollTop = tableEl.scrollTop;
      requestAnimationFrame(() => { syncRef.current = null; });
    };
    const fromGantt = () => {
      if (syncRef.current === 'table') return;
      syncRef.current = 'gantt';
      tableEl.scrollTop = ganttEl.scrollTop;
      requestAnimationFrame(() => { syncRef.current = null; });
    };
    tableEl.addEventListener('scroll', fromTable);
    ganttEl.addEventListener('scroll', fromGantt);
    return () => {
      tableEl.removeEventListener('scroll', fromTable);
      ganttEl.removeEventListener('scroll', fromGantt);
    };
  }, [zoom, tableScrollRef, groups]);

  // Horizontal sync header
  useEffect(() => {
    const ganttEl = bodyRef.current;
    const headerEl = headerRef.current;
    if (!ganttEl || !headerEl) return;
    const sync = () => { headerEl.scrollLeft = ganttEl.scrollLeft; };
    ganttEl.addEventListener('scroll', sync);
    return () => ganttEl.removeEventListener('scroll', sync);
  }, []);

  // Auto-scroll to today
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, todayX - el.clientWidth / 3);
  }, [zoom]); // eslint-disable-line

  // Build header
  const months = [];
  const dayCells = [];
  const cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    const mStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const mEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const s = Math.max(0, Math.round((mStart - rangeStart) / 86400000));
    const e = Math.min(totalDays, Math.round((mEnd - rangeStart) / 86400000) + 1);
    const w = (e - s) * pxPerDay;
    if (w > 0) {
      months.push(
        <div key={`${cursor.getMonth()}-${cursor.getFullYear()}`} className="rp-gantt-month" style={{ width: w, minWidth: w }}>
          {w > 60 ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}` : MONTHS[cursor.getMonth()]}
        </div>
      );
    }
    cursor.setMonth(cursor.getMonth() + 1);
    cursor.setDate(1);
  }

  if (zoom === 'weeks') {
    const dc = new Date(rangeStart);
    for (let i = 0; i < totalDays; i++) {
      const isToday = toISODate(dc) === todayStr;
      const isWknd = dc.getDay() === 0 || dc.getDay() === 6;
      dayCells.push(
        <div key={i} className={`rp-gantt-day${isWknd ? ' weekend' : ''}${isToday ? ' today' : ''}`}
          style={{ width: pxPerDay, minWidth: pxPerDay }}>
          {pxPerDay >= 16 ? dc.getDate() : (i % 7 === 0 ? dc.getDate() : '')}
        </div>
      );
      dc.setDate(dc.getDate() + 1);
    }
  } else {
    const dc = new Date(rangeStart);
    for (let i = 0; i < totalDays;) {
      if (dc.getDay() === 1 || dc.getDate() === 1) {
        const skip = zoom === 'quarters' ? 28 : 7;
        dayCells.push(
          <div key={i} className="rp-gantt-day" style={{ width: pxPerDay * skip, minWidth: pxPerDay * skip }}>
            {dc.getDate()}
          </div>
        );
        dc.setDate(dc.getDate() + skip);
        i += skip;
      } else {
        dc.setDate(dc.getDate() + 1);
        i++;
      }
    }
  }

  // Grid lines
  const gridLines = [];
  const lc = new Date(rangeStart);
  for (let i = 0; i < totalDays; i++) {
    if (lc.getDate() === 1) gridLines.push(<div key={`m${i}`} className="rp-gantt-gridline month" style={{ left: i * pxPerDay }} />);
    else if (lc.getDay() === 1 && zoom !== 'quarters') gridLines.push(<div key={`w${i}`} className="rp-gantt-gridline" style={{ left: i * pxPerDay }} />);
    lc.setDate(lc.getDate() + 1);
  }

  const ROW_H = 34;
  const GROUP_H = 38;
  const ADDROW_H = 28;
  const BAR_H = 20;

  const ganttRows = [];
  groups.forEach(g => {
    const expanded = g.expanded !== false;
    const rowDates = (g.rows || []).filter(r => r.start_date && r.end_date).map(r => ({
      s: toX(effectiveStarts[r.id] || r.start_date),
      e: toX(effectiveDates[r.id] || r.end_date),
    }));
    const gMin = rowDates.length ? Math.min(...rowDates.map(d => d.s)) : 0;
    const gMax = rowDates.length ? Math.max(...rowDates.map(d => d.e)) : 0;

    ganttRows.push(
      <div key={`gg-${g.id}`} className="rp-gantt-group-row" style={{ height: GROUP_H }}>
        {!g.is_milestone_group && rowDates.length > 0 && (
          <div className="rp-gantt-group-bar" style={{ left: gMin, width: Math.max(gMax - gMin, 4), background: g.color, opacity: 0.35 }} />
        )}
      </div>
    );

    if (expanded) {
      (g.rows || []).forEach(r => {
        if (r.is_milestone) {
          const mx = toX(r.start_date || r.end_date);
          ganttRows.push(
            <div key={r.id} className="rp-gantt-row" style={{ height: ROW_H }}>
              <div
                className="rp-gantt-milestone-dia"
                style={{ left: mx - 7, background: g.color || 'var(--color-brand)' }}
                title={`${r.name} — ${fmtDate(r.start_date || r.end_date)}`}
              />
            </div>
          );
        } else {
          const effEnd = effectiveDates[r.id] || r.end_date;
          const effStart = effectiveStarts[r.id] || r.start_date;
          const isDelayed = r.actual_end_date && r.end_date && r.actual_end_date > r.end_date;
          const isShifted = effStart !== r.start_date;
          const sx = toX(effStart);
          const ex = toX(effEnd);
          const plannedSx = toX(r.start_date);
          const plannedEx = toX(r.end_date);
          const bw = Math.max(ex - sx, 8);
          const pct = r.percent_complete || 0;
          ganttRows.push(
            <div key={r.id} className="rp-gantt-row" style={{ height: ROW_H }}>
              {/* Planned bar (shown as faded overlay when delayed or shifted by predecessor) */}
              {(isDelayed || isShifted) && r.start_date && r.end_date && (
                <div
                  className="rp-gantt-bar"
                  style={{ left: plannedSx, width: Math.max(plannedEx - plannedSx, 8), height: BAR_H, borderRadius: 4, opacity: 0.3, pointerEvents: 'none' }}
                >
                  <div className="rp-gantt-bar-bg" style={{ background: g.color, borderRadius: 4 }} />
                </div>
              )}
              {/* Actual/effective bar */}
              <div
                className="rp-gantt-bar"
                style={{ left: sx, width: bw, height: BAR_H, borderRadius: 4, outline: isDelayed ? '1.5px solid #dc2626' : isShifted ? '1.5px solid #f97316' : undefined }}
                title={`${r.name} — ${fmtDate(effStart)} → ${fmtDate(effEnd)}${isDelayed ? ' (delayed)' : isShifted ? ' (shifted by predecessor)' : ''} (${pct}%)`}
              >
                <div className="rp-gantt-bar-bg" style={{ background: isDelayed ? '#fca5a5' : isShifted ? '#fed7aa' : g.color, borderRadius: 4 }} />
                <div className="rp-gantt-bar-fill" style={{ width: `${pct}%`, background: g.color, borderRadius: 4 }} />
                <span className="rp-gantt-bar-lbl">{r.name}</span>
              </div>
            </div>
          );
        }
      });
      // add row spacer
      ganttRows.push(<div key={`addr-${g.id}`} style={{ height: ADDROW_H }} />);
    }
  });

  const totalH = groups.reduce((h, g) => {
    const expanded = g.expanded !== false;
    return h + GROUP_H + (expanded ? (g.rows || []).length * ROW_H + ADDROW_H : 0);
  }, 0);

  return (
    <div className="rp-gantt-panel">
      <div className="rp-gantt-header" ref={headerRef}>
        <div className="rp-gantt-months" style={{ width: totalW, minWidth: totalW }}>{months}</div>
        <div className="rp-gantt-days" style={{ width: totalW, minWidth: totalW }}>{dayCells}</div>
      </div>
      <div className="rp-gantt-body" ref={bodyRef}>
        <div className="rp-gantt-inner" style={{ height: totalH, width: totalW, position: 'relative' }}>
          {gridLines}
          {todayX >= 0 && todayX <= totalW && <div className="rp-gantt-today-line" style={{ left: todayX }} />}
          {ganttRows}
        </div>
      </div>
    </div>
  );
};

// ── Spreadsheet Gantt ──────────────────────────────────────────────────────────

const SpreadsheetGantt = ({ doc, groups, zoom, onEditRow }) => {
  const dispatch = useDispatch();
  const tableBodyRef = useRef(null);
  const [editingRow, setEditingRow] = useState(null);

  const toggleGroup = (group) => {
    dispatch(updateGroup({ id: group.id, data: { expanded: !(group.expanded !== false) } }));
  };

  const handleAddRow = (groupId, isMilestone = false) => {
    const todayIso = toISODate(today());
    const endIso = toISODate(addDays(today(), 7));
    if (onEditRow) {
      onEditRow({
        _isNew: true,
        groupId,
        is_milestone: isMilestone,
        name: isMilestone ? 'New Milestone' : 'New Task',
        start_date: todayIso,
        end_date: isMilestone ? todayIso : endIso,
        status: 'not-started',
        percent_complete: 0,
        custom_fields: {},
      });
    }
  };

  const handleRowUpdate = async (rowId, field, value) => {
    try {
      await dispatch(updateRow({ id: rowId, data: { [field]: value } })).unwrap();
    } catch (e) {
      toast.error(e || 'Failed to update');
    }
  };

  const handleDeleteRow = async (rowId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this row?')) return;
    try {
      await dispatch(deleteRow({ id: rowId })).unwrap();
    } catch (err) {
      toast.error(err || 'Failed to delete row');
    }
  };

  const handleDeleteGroup = async (groupId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this group and all its rows?')) return;
    try {
      await dispatch(deleteGroup({ id: groupId })).unwrap();
    } catch (err) {
      toast.error(err || 'Failed to delete group');
    }
  };

  return (
    <div className="rp-split">
      {/* Table panel */}
      <div className="rp-table-panel">
        <div className="rp-col-header">
          <div style={{ width: 20, flexShrink: 0 }} />
          <div className="rp-col-header-cell name-col">Task Name</div>
          <div className="rp-col-header-cell assignee-col">Assignee</div>
          <div className="rp-col-header-cell date-col">Start</div>
          <div className="rp-col-header-cell date-col end-date">Days</div>
          <div className="rp-col-header-cell pct-col">% Done</div>
        </div>

        <div className="rp-table-body" ref={tableBodyRef}>
          {groups.length === 0 && (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
              No groups yet — click <strong>Add Group</strong> to get started, or use <strong>Import</strong> to pull from a project.
            </div>
          )}
          {groups.map(g => {
            const expanded = g.expanded !== false;
            const rows = g.rows || [];
            const taskRows = rows.filter(r => !r.is_milestone);
            const totalPct = taskRows.length
              ? Math.round(taskRows.reduce((s, r) => s + (r.percent_complete || 0), 0) / taskRows.length)
              : 0;
            return (
              <React.Fragment key={g.id}>
                {/* Group row */}
                {g.is_milestone_group ? (
                  <div
                    className="rp-group-row rp-milestone-group-header"
                    style={{ '--group-color': 'transparent', cursor: 'default' }}
                  >
                    <div style={{ width: 20, flexShrink: 0 }} />
                    <div className="rp-cell-name group-name">
                      <span className="rp-name-text" style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.55 }}>
                        {g.name}
                      </span>
                    </div>
                    <div className="rp-cell assignee-cell" />
                    <div className="rp-cell date-cell" />
                    <div className="rp-cell date-cell end-date" />
                    <div className="rp-cell pct-cell" />
                    <div className="rp-row-actions" />
                  </div>
                ) : (
                  <div
                    className="rp-group-row"
                    style={{ '--group-color': g.color }}
                    onClick={() => toggleGroup(g)}
                  >
                    <button
                      className={`rp-row-expand${expanded ? ' open' : ''}`}
                      onClick={(e) => { e.stopPropagation(); toggleGroup(g); }}
                    >
                      <svg viewBox="0 0 16 16" fill="currentColor"><path d="M6 3.5l4.5 4.5L6 12.5V3.5z"/></svg>
                    </button>
                    <div className="rp-cell-name group-name">
                      <span className="rp-name-text">{g.name}</span>
                    </div>
                    <div className="rp-cell assignee-cell" />
                    <div className="rp-cell date-cell" />
                    <div className="rp-cell date-cell end-date" />
                    <div className="rp-cell pct-cell">
                      {taskRows.length > 0 && (
                        <>
                          <MiniBar pct={totalPct} color={g.color} />
                          <span className="rp-pct-text">{totalPct}%</span>
                        </>
                      )}
                    </div>
                    <div className="rp-row-actions">
                      <button
                        className="rp-row-action-btn"
                        onClick={(e) => handleDeleteGroup(g.id, e)}
                        title="Delete group"
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                          <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 010-2h3a1 1 0 011-1h3a1 1 0 011 1h3a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Task rows */}
                {expanded && rows.map(r => {
                  const isEditing = editingRow === r.id;
                  return (
                    <div
                      key={r.id}
                      className="rp-task-row"
                      onClick={() => !isEditing && onEditRow && onEditRow(r)}
                    >
                      <div style={{ width: 20, flexShrink: 0 }} />
                      <div
                        className="rp-cell-name task-name"
                        onDoubleClick={(e) => { e.stopPropagation(); setEditingRow(r.id); }}
                      >
                        {r.is_milestone && (
                          <div className="rp-milestone-diamond" style={{ background: g.color || 'var(--color-brand)' }} title="Milestone" />
                        )}
                        {isEditing ? (
                          <input
                            className="rp-cell-input"
                            defaultValue={r.name}
                            autoFocus
                            onBlur={(e) => {
                              if (e.target.value.trim() && e.target.value !== r.name) {
                                handleRowUpdate(r.id, 'name', e.target.value.trim());
                              }
                              setEditingRow(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                if (e.target.value.trim() && e.target.value !== r.name) {
                                  handleRowUpdate(r.id, 'name', e.target.value.trim());
                                }
                                setEditingRow(null);
                              }
                              if (e.key === 'Escape') setEditingRow(null);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="rp-name-text" style={{ color: r.is_milestone ? g.color : undefined }}>
                            {r.name}
                          </span>
                        )}
                      </div>
                      <div className="rp-cell assignee-cell">
                        <AssigneeCell customName={r.custom_fields?.assignee_name || (r.assignee ? `${r.assignee.first_name || ''} ${r.assignee.last_name || ''}`.trim() : '')} />
                      </div>
                      <div className="rp-cell date-cell">{fmtDate(r.start_date)}</div>
                      <div className="rp-cell date-cell end-date">
                        {r.is_milestone ? '—' : (() => {
                          const days = r.start_date && r.end_date ? (daysBetween(parseDate(r.start_date), parseDate(r.end_date)) + 1) + 'd' : '—';
                          const isLate = r.actual_end_date && r.end_date && r.actual_end_date > r.end_date;
                          const lateBy = isLate ? Math.round((new Date(r.actual_end_date + 'T12:00:00') - new Date(r.end_date + 'T12:00:00')) / 86400000) : 0;
                          return (
                            <>
                              {days}
                              {isLate && (
                                <span style={{ marginLeft: 4, fontSize: '9px', color: '#dc2626', fontWeight: 600, background: '#fee2e2', borderRadius: 3, padding: '0 3px' }}>
                                  +{lateBy}d
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="rp-cell pct-cell">
                        {!r.is_milestone && (
                          <>
                            <MiniBar pct={r.percent_complete || 0} color={g.color} />
                            <span className="rp-pct-text">{r.percent_complete || 0}%</span>
                          </>
                        )}
                      </div>
                      <div className="rp-row-actions">
                        <button
                          className="rp-row-action-btn"
                          onClick={(e) => { e.stopPropagation(); onEditRow && onEditRow(r); }}
                          title="Edit details"
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10z"/>
                          </svg>
                        </button>
                        <button
                          className="rp-row-action-btn danger"
                          onClick={(e) => handleDeleteRow(r.id, e)}
                          title="Delete row"
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 010-2h3a1 1 0 011-1h3a1 1 0 011 1h3a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add row buttons */}
                {expanded && (
                  <div className="rp-addrow">
                    {!g.is_milestone_group && (
                      <button className="rp-addrow-btn" onClick={() => handleAddRow(g.id, false)}>
                        <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                          <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/>
                        </svg>
                        Add task
                      </button>
                    )}
                    <button className="rp-addrow-btn" onClick={() => handleAddRow(g.id, true)}>
                      <span className="rp-milestone-diamond" style={{ background: g.color || 'var(--color-brand)' }} />
                      Add milestone
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Gantt */}
      <GanttPane groups={groups} zoom={zoom} tableScrollRef={tableBodyRef} dependencies={doc?.dependencies || []} />
    </div>
  );
};

export const TimelineView = ({ groups, zoom, dependencies = [] }) => {
  const fakeRef = useRef(null);
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <GanttPane groups={groups} zoom={zoom} tableScrollRef={fakeRef} dependencies={dependencies} />
    </div>
  );
};

export default SpreadsheetGantt;
